import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Bell,
  Users,
  Smartphone,
  CreditCard,
  Shield,
  Clock,
  Globe,
  BarChart3,
  Palette,
  RefreshCw,
  MessageSquare,
  Contact,
} from 'lucide-react';
import { Link } from 'wouter';
import { LandingLayout } from '../shared/LandingLayout';
import { SEOHead } from '../shared/SEOHead';
import { LandingPricing } from '../shared/LandingPricing';
import { LandingCTA } from '../shared/LandingCTA';
import { TrustSection } from '../shared/TrustSection';
import {
  generateCanonicalUrl,
  combineSchemas,
  generateOrganizationSchema,
  generateWebPageSchema,
} from '@/utils/seo';

export default function FeaturesPage() {
  const { t } = useTranslation(['features', 'common']);

  const structuredData = combineSchemas(
    generateOrganizationSchema(),
    generateWebPageSchema({
      name: 'Features - Online Booking Software | Sloty',
      description: t('features:hero.subtitle'),
      url: generateCanonicalUrl('/features'),
    })
  );

  const featureCategories = [
    {
      id: 'booking',
      title: t('features:categories.booking.title'),
      description: t('features:categories.booking.description'),
      features: [
        {
          icon: Calendar,
          title: t('features:categories.booking.features.onlineBooking.title'),
          description: t('features:categories.booking.features.onlineBooking.description'),
        },
        {
          icon: Clock,
          title: t('features:categories.booking.features.availability.title'),
          description: t('features:categories.booking.features.availability.description'),
        },
        {
          icon: Bell,
          title: t('features:categories.booking.features.reminders.title'),
          description: t('features:categories.booking.features.reminders.description'),
        },
        {
          icon: Smartphone,
          title: t('features:categories.booking.features.mobileFriendly.title'),
          description: t('features:categories.booking.features.mobileFriendly.description'),
        },
      ],
    },
    {
      id: 'management',
      title: t('features:categories.management.title'),
      description: t('features:categories.management.description'),
      features: [
        {
          icon: Users,
          title: t('features:categories.management.features.staffManagement.title'),
          description: t('features:categories.management.features.staffManagement.description'),
        },
        {
          icon: RefreshCw,
          title: t('features:categories.management.features.calendarSync.title'),
          description: t('features:categories.management.features.calendarSync.description'),
        },
        {
          icon: MessageSquare,
          title: t('features:categories.management.features.customQuestions.title'),
          description: t('features:categories.management.features.customQuestions.description'),
        },
        {
          icon: BarChart3,
          title: t('features:categories.management.features.analytics.title'),
          description: t('features:categories.management.features.analytics.description'),
        },
        {
          icon: Contact,
          title: t('features:categories.management.features.customerDatabase.title'),
          description: t('features:categories.management.features.customerDatabase.description'),
        },
      ],
    },
    {
      id: 'customization',
      title: t('features:categories.customization.title'),
      description: t('features:categories.customization.description'),
      features: [
        {
          icon: Palette,
          title: t('features:categories.customization.features.brandedPage.title'),
          description: t('features:categories.customization.features.brandedPage.description'),
        },
        {
          icon: Globe,
          title: t('features:categories.customization.features.multiLanguage.title'),
          description: t('features:categories.customization.features.multiLanguage.description'),
        },
        {
          icon: CreditCard,
          title: t('features:categories.customization.features.payments.title'),
          description: t('features:categories.customization.features.payments.description'),
        },
        {
          icon: Shield,
          title: t('features:categories.customization.features.privacy.title'),
          description: t('features:categories.customization.features.privacy.description'),
        },
      ],
    },
  ];

  return (
    <LandingLayout transparentHeader>
      <SEOHead
        title={t('common:seo.features.title')}
        description={t('common:seo.features.description')}
        keywords="booking software features, appointment scheduling features, online booking system, calendar sync, staff management, automated reminders"
        canonical={generateCanonicalUrl('/features')}
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="py-20 sm:py-28 bg-gradient-to-b from-brand-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
            {t('features:hero.title')}
          </h1>
          <p className="mt-6 text-xl text-slate-600 max-w-2xl mx-auto">
            {t('features:hero.subtitle')}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/register"
              className="inline-flex items-center justify-center rounded-full py-3 px-8 text-sm font-semibold bg-brand-600 text-white hover:bg-brand-500"
            >
              {t('features:hero.cta')}
            </Link>
            <a
              href="/#pricing"
              className="inline-flex items-center justify-center rounded-full py-3 px-8 text-sm font-semibold ring-1 ring-slate-300 text-slate-700 hover:ring-slate-400"
            >
              {t('features:hero.ctaSecondary')}
            </a>
          </div>
          {/* TODO: Add product screenshot/mockup of the booking calendar here for visual impact */}
        </div>
      </section>

      {/* Feature Categories */}
      {featureCategories.map((category, catIndex) => (
        <section
          key={category.id}
          className={`py-16 sm:py-20 ${catIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                {category.title}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {category.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {category.features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                  >
                    <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-brand-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-slate-600">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      <TrustSection />

      <LandingPricing />

      <LandingCTA
        title={t('features:cta.title')}
        subtitle={t('features:cta.subtitle')}
        ctaPrimary={t('features:cta.button')}
        ctaPrimaryHref="/register"
        ctaSecondary={t('features:cta.secondaryButton')}
        ctaSecondaryHref="/#pricing"
        variant="gradient"
      />
    </LandingLayout>
  );
}
